import React, { useState } from 'react';
import ReactPlayer from 'react-player';
import { FaPlay, FaEdit, FaSave, FaSyncAlt, FaCalendarAlt, FaTimes } from 'react-icons/fa';
import { FiShare, FiCheckCircle, FiAlertCircle } from 'react-icons/fi';
import { motion } from 'framer-motion';

const ReelContent = ({
  user,
  generatedContent,
  isPlaying,
  setIsPlaying,
  isEditingContent,
  editedContent,
  setEditedContent,
  handleToggleEdit,
  isPublishing,
  handlePublish,
  handleScheduleLater,
  publishStatus,
  contentTimestamp,
  handleClearContent,
  TEST_VIDEO_URL
}) => {
  const [isTextExpanded, setIsTextExpanded] = useState(false);

  // Function to truncate text to first line
  const truncateText = (text) => {
    if (!text) return '';
    const firstLine = text.split('\n')[0];
    return firstLine.length > 100 ? firstLine.substring(0, 100) + '...' : firstLine;
  };

  // Function to check if text needs truncation
  const needsTruncation = (text) => {
    if (!text) return false;
    return text.includes('\n') || text.length > 100;
  };

  return (
    <div className="glass-effect rounded-2xl border border-gray-600/30 relative">
      {/* Clear Session Button - Inside content area */}
      <div className="absolute top-3 right-3 z-10">
        <button
          onClick={handleClearContent}
          className="flex items-center gap-2 px-3 py-2 bg-gray-800/50 hover:bg-gray-700/50 border border-gray-600/30 rounded-lg text-white text-sm font-medium transition-all duration-200 hover:scale-105"
          title="Clear all content and start over"
        >
          <FaTimes className="text-xs" />
        </button>
      </div>
      
      {/* Instagram Post Preview */}
      <div className="bg-black/50 rounded-xl overflow-hidden">
        {/* Post Header */}
        <div className="p-3 border-gray-700/50">
          {/* Header content can be added here if needed */}
        </div>
        
        {/* Post Video */}
        <div className="relative" style={{ maxWidth: '40%', margin: '0 auto' }}>
          <div
            className="video-player-container"
            style={{
              width: '100%',
              maxHeight: '200px',
              aspectRatio: '9 / 16',
              background: 'black',
              borderRadius: '8px',
              overflow: 'hidden',
              position: 'relative',
            }}
          >
            <ReactPlayer
              url={generatedContent?.images[0]?.url || TEST_VIDEO_URL}
              playing={isPlaying}
              width="100%"
              height="100%"
              playsinline={true}
              controls={true}
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '100%',
                height: '100%',
              }}
              config={{
                file: {
                  attributes: {
                    style: {
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    },
                  },
                },
              }}
            />
            <div
              className="video-controls-overlay"
              onClick={() => setIsPlaying(!isPlaying)}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                display: isPlaying ? 'none' : 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                cursor: 'pointer',
              }}
            >
              {!isPlaying && (
                <div className="play-button">
                  <FaPlay className="play-icon" />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Post Content */}
        <div className="p-3">
          <div className="mb-3">
            {isEditingContent ? (
              <textarea
                value={editedContent}
                onChange={(e) => setEditedContent(e.target.value)}
                className="w-full bg-gray-900/50 border border-gray-600/50 rounded-lg px-3 py-2 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none text-sm"
                style={{ 
                  minHeight: "80px",
                  whiteSpace: "pre-wrap"
                }}
              />
            ) : (
              <div className="text-white">
                <div className="whitespace-pre-wrap text-sm">
                  {isTextExpanded 
                    ? (generatedContent?.content || '')
                    : truncateText(generatedContent?.content || '')
                  }
                </div>
                {needsTruncation(generatedContent?.content) && (
                  <button
                    onClick={() => setIsTextExpanded(!isTextExpanded)}
                    className="text-blue-400 hover:text-blue-300 text-xs mt-1 underline"
                  >
                    {isTextExpanded ? 'Show less' : 'Show more'}
                  </button>
                )}
              </div>
            )}
          </div>
          
          <div className="flex justify-end">
            <button
              type="button"
              onClick={handleToggleEdit}
              className={`px-3 py-1.5 rounded-lg font-medium transition-all duration-200 flex items-center gap-1.5 text-sm ${
                isEditingContent
                  ? 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white'
                  : 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white'
              }`}
            >
              {isEditingContent ? (
                <>
                  <FaSave className="text-xs" />
                  Save
                </>
              ) : (
                <>
                  <FaEdit className="text-xs" />
                  Edit
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReelContent; 